import { useCallback, useRef, useState } from 'react';
import * as React from 'react';
import { noop, partition } from 'lodash';
import type {
  Account,
  DeleteProjectsMutationResult,
  Project,
  ProjectConnection,
  Workspace,
} from '@frameio/graphql-client/generated';
import { useDeleteProjectsMutation } from '@frameio/graphql-client/generated';
import { plural } from '@frameio/i18n';
import type { ToastAlertProps } from '@frameio/vapor';
import { useTrackUnsafe } from '../../../segment/hooks';
import type { TrackingSourceType } from '../../../segment/hooks/utils/trackingUtils';
import type { CancelDeletedActiveUploadsArgs } from '../../../transfers/types';
import {
  DeleteConfirmation,
  truncateName,
  useLoadingToast,
} from '../../shared';
import TEXT from './generated/TEXT/useDeleteProjects';

export type DeleteableProject = Pick<Project, 'id' | 'name'>;

type DeleteProjectsOpts = {
  onSuccess?: (projects: Project[]) => void;
  trackingSource: TrackingSourceType;
};

type DeleteProjectsReturn = {
  deleteDialog: React.ReactElement<any>;
  showDeleteConfirmation: (projects: DeleteableProject[]) => void;
};

/**
 * The useDeleteProjects hook can be used to delete one or many projects,
 * including a step where the user must confirm the deletion by typing in the
 * word "delete." A toast is then displayed if the mutation succeeds or fails.
 *
 * @param opts.onSuccess an optional callback fired if the delete succeeds
 * @param opts.trackingSource
 * @return the DeleteConfirmation dialog to render and a function for requesting a delete
 */
export function useDeleteProjects(
  uploaderWorker: {
    worker: {
      cancelAnyActiveUploadsDeleted(
        jobsToDelete: CancelDeletedActiveUploadsArgs
      );
    };
  },
  opts: DeleteProjectsOpts = {
    onSuccess: noop,
    trackingSource: '',
  }
): DeleteProjectsReturn {
  const { onSuccess = noop, trackingSource } = opts;
  const [projectsToDelete, setProjectsToDelete] = useState<DeleteableProject[]>(
    []
  );
  /**
   * Want to retain this message so it doesn't change in the dialog while the
   * dialog close animation is playing.
   */
  const confirmationMessage = useRef<React.ReactNode>(null);
  const [deleteProjects] = useDeleteProjectsMutation();
  const { addToast, updateAndHideToast } = useLoadingToast();
  const track = useTrackUnsafe();
  const count = projectsToDelete.length;

  // Extract TEXT properties to avoid calling hooks inside callbacks
  const textDeleteProjectLoading = TEXT.deleteProjectLoading;
  const textSuccessMessage = TEXT.successMessage;
  const textErrorMessage = TEXT.errorMessage;
  const textGenericErrorMessage = TEXT.genericErrorMessage;
  const textConfirmationMessage = TEXT.confirmationMessage;

  const handleConfirm = () => {
    const toastId = addToast(textDeleteProjectLoading);

    track(
      'projects-deleted',
      { projects_count: count, source: trackingSource },
      { logToDatadog: true }
    );

    const handleResolve = (result: DeleteProjectsMutationResult) => {
      const { name } = projectsToDelete[0];
      let title = textSuccessMessage(count, truncateName(name, 15));
      let variant: ToastAlertProps['variant'] = 'success';

      if (result.error) {
        title = textErrorMessage(count, name);
        variant = 'error';
      } else {
        const projects = result.data.deleteProjects.result.map(
          (payload) => payload.result as Project
        );

        onSuccess(projects);
        uploaderWorker.worker.cancelAnyActiveUploadsDeleted({
          projectId: projectsToDelete[0].id,
          deletedIds: null,
        });
      }

      updateAndHideToast(toastId, {
        title,
        variant,
      });
    };

    const handleReject = () => {
      updateAndHideToast(toastId, {
        title: textGenericErrorMessage,
        variant: 'error',
      });
    };

    deleteProjects({
      variables: {
        input: {
          ids: projectsToDelete.map((p: DeleteableProject) => p.id),
        },
      },
      update(proxy, response) {
        if (!response?.data || !response?.data?.deleteProjects.successful)
          return;

        const { result } = response.data.deleteProjects;

        if (!result) return;

        if (result.some((projectResult) => !projectResult?.successful)) {
          return;
        }

        const first = result[0];

        if (first === null || first === undefined || !first.result) return;

        const workspace = first.result.workspace as Workspace;
        const workspaceId = workspace.id;
        const accountId = workspace.accountId;
        const deletedProjectIds = result.map(
          (projectResult) => projectResult?.result?.id
        );
        const [deletedInactiveProjects, deletedActiveProjects] = partition(
          result,
          (deletedProjects) => Boolean(deletedProjects?.result?.archivedAt)
        );

        proxy.modify<Account>({
          id: proxy.identify({
            __typename: 'Account',
            id: accountId,
          }),
          fields: {
            projects(value: ProjectConnection, { readField }) {
              const newNodes = value.nodes.filter(
                (project) =>
                  !deletedProjectIds.includes(readField('id', project))
              );

              return {
                ...value,
                nodes: newNodes,
                totalCount: newNodes.length,
              };
            },
          },
        });

        proxy.modify<Workspace>({
          id: proxy.identify({
            __typename: 'Workspace',
            id: workspaceId,
          }),
          fields: {
            projectCount: (value) => value - deletedProjectIds.length,
            projects(value: ProjectConnection, { readField }) {
              const newNodes = value.nodes.filter(
                (project) =>
                  !deletedProjectIds.includes(readField('id', project))
              );

              return {
                ...value,
                nodes: newNodes,
                totalCount: newNodes.length,
              };
            },
            visibleActiveProjectCount(value) {
              if (!value) return value;
              return Math.max(0, value - deletedActiveProjects.length);
            },
            visibleInactiveProjectCount(value) {
              if (!value) return value;
              return Math.max(0, value - deletedInactiveProjects.length);
            },
          },
        });
      },
    }).then(handleResolve, handleReject);
  };

  const deleteDialog = (
    <DeleteConfirmation
      confirmButtonText={TEXT.deleteProjectConfirmButton(count)}
      isShowing={count > 0}
      message={confirmationMessage.current}
      onConfirm={handleConfirm}
      onRequestClose={() => {
        setProjectsToDelete([]);
      }}
      stackActionButtons={true}
      title={TEXT.deleteProjectTitle(count)}
    />
  );

  const showDeleteConfirmation = useCallback(
    (projects: DeleteableProject[]) => {
      confirmationMessage.current = textConfirmationMessage(
        projects.length,
        truncateName(projects[0]?.name, 30)
      );
      setProjectsToDelete(projects);
    },
    [textConfirmationMessage]
  );

  return {
    deleteDialog,
    showDeleteConfirmation,
  };
}

TEXT.DEFINE({
  /** Confirmation message */
  confirmationMessage: (count: number, name: string) => {
    const isSingular = count === 1;
    return isSingular ? (
      <>
        Are you sure you want to delete <strong>"{name}"</strong>? Admins will
        be able to restore the project for 30 days. After 30 days, all
        associated media will be permanently deleted.
      </>
    ) : (
      <>
        Are you sure you want to delete {count} Projects? All associated media
        will be permanently deleted. This cannot be undone.
      </>
    );
  },
  /** Delete project button label */
  deleteProjectConfirmButton: (count: number): string =>
    plural(count, `Delete ${count} Projects`, 'Delete Project'),
  /** Delete project loading toast */
  deleteProjectLoading: 'Deleting Project…',
  /** Delete project confirmation title */
  deleteProjectTitle: (count: number): string =>
    plural(count, `Delete ${count} Projects?`, 'Delete Project?'),
  /** Error message with name */
  errorMessage: (count: number, name: string): string =>
    plural(
      count,
      `Failed to delete ${count} Projects`,
      `Failed to delete the Project "${name}"`
    ),
  /** Generic error message */
  genericErrorMessage: 'Failed to delete the Project',
  /** Success message */
  successMessage: (count: number, name: string): string =>
    plural(
      count,
      `${count} Projects and all their files have been deleted`,
      `"${name}" Project was deleted`
    ),
});
